import React, { useCallback, useEffect, useState } from 'react';

import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { MediaItem } from '../../../openapi/leo';
import {
  createTicket,
  setIsLatestLeoResponseExtensive,
} from '../../../slices/LeoSlice';
import { AppDispatch, CurrentChat, RootState } from '../../../types';
import { cn } from '../../../utils/classUtils';
import { delay } from '../../../utils/commonUtils';
import { getLeoMediaUrl } from '../../../utils/ImageUtils';
import Button from '../../Button';
import NeoLeoGenericShareables from '../Shareables/NeoLeoGenericShareables';
import Markdown from './MarkDownRender';
import NeoLeoChatResponseActions from './NeoLeoChatResponseAction';
import NeoLeoTxnOrListingItems from './NeoLeoTxnOrListingItems';

import { FeedbackNote } from './FeedbackNote';
import { NeoLeoChatBubble } from './NeoChatBubble';
import NeoLeoCallSupportMsg from './NeoLeoCallSupportMsg';
import NeoLeoCourses from './NeoLeoCourses';
import NeoLeoCreateSupportTicketMsgV2 from './NeoLeoCreateSupportTicketMsgV2';
import NeoLeoLearningLinks from './NeoLeoLearningLinks';

interface NeoLeoResponseProps extends CurrentChat {
  isShowingHistory: boolean;
  isDisclaimer: boolean;
}

enum ContextEnum {
  TEXT_RESPONSE = 'TEXT_RESPONSE',
  TRANSACTION_ITEMS = 'TRANSACTION_ITEMS',
  COURSES = 'COURSES',
  LEARNING_LINKS = 'LEARNING_LINKS',
  SHAREABLE_POSTERS = 'SHAREABLE_POSTERS',
  NOTE = 'NOTE',
  CREATE_SUPPORT_TICKET = 'CREATE_SUPPORT_TICKET',
  CREATED_SUPPORT_TICKET = 'CREATED_SUPPORT_TICKET',
  CALL_SUPPORT = 'CALL_SUPPORT',
}

const NeoLeoResponse: React.FC<NeoLeoResponseProps> = ({
  isShowingHistory,
  isDisclaimer,
  ...msg
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    msgContext: context,
    isCreatingTicket,
    isTicketCreated,
  } = useSelector((state: RootState) => state.leo);
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const isLatestResponse = msg.id === context?.bot_message_id;
  const EXPANSIVE_THRESHOLD = 400;
  const isResponseExtensive = msg.message
    ? msg.message.length > EXPANSIVE_THRESHOLD
    : false;

  const ContextOrder = {
    First: ContextEnum.TEXT_RESPONSE,
    Last: ContextEnum.TEXT_RESPONSE,
  };

  const msgContext = msg?.id === context?.bot_message_id ? context : undefined;
  const showCreateTicket =
    isLatestResponse &&
    !isTicketCreated &&
    (!!msgContext?.create_ticket || msg.vote_type === 'DOWNVOTE');

  const showCreatedSupportTicketMsg =
    msgContext?.support_flow_details?.ticket?.is_created;
  if (showCreatedSupportTicketMsg) {
    ContextOrder.Last = ContextEnum.CREATED_SUPPORT_TICKET;
  }

  const showCreateSupportTicket =
    msgContext?.support_flow_details?.ticket?.can_create;
  if (showCreateSupportTicket) {
    ContextOrder.Last = ContextEnum.CREATE_SUPPORT_TICKET;
  }

  const canInitiateCallSupport =
    msgContext?.support_flow_details?.call?.can_initiate;
  const callSupportDetails =
    msgContext?.support_flow_details?.call?.roar_items?.[0];
  const showCallSupport = canInitiateCallSupport && !!callSupportDetails;
  if (showCallSupport) {
    ContextOrder.Last = ContextEnum.CALL_SUPPORT;
  }

  let shareablePosters: MediaItem[] = [];
  let hasShareablePosters = false;
  if (msgContext?.media) {
    shareablePosters = msgContext.media
      ?.filter((el) => !!el.media_url && el.type !== 'SHARRAN')
      .map((el) => ({
        ...el,
        media_url: getLeoMediaUrl(el.media_url)!,
      }));
  }

  if (shareablePosters.length > 0) {
    ContextOrder.Last = ContextEnum.SHAREABLE_POSTERS;
    hasShareablePosters = true;
  }

  const hasTransactionItems = !!msgContext?.details?.[0]?.items?.length;
  if (hasTransactionItems) {
    ContextOrder.Last = ContextEnum.TRANSACTION_ITEMS;
  }

  const hasCourses = !!msgContext?.references?.courses?.length;
  if (hasCourses) {
    ContextOrder.Last = ContextEnum.COURSES;
  }

  const hasLearningLinks = !!msgContext?.references?.documents?.length;
  if (hasLearningLinks) {
    ContextOrder.Last = ContextEnum.LEARNING_LINKS;
  }

  if (msgContext?.note) {
    ContextOrder.Last = ContextEnum.NOTE;
  }

  const handleExtensiveResponse = useCallback(async () => {
    if (isLatestResponse) {
      dispatch(setIsLatestLeoResponseExtensive(isResponseExtensive));
      await delay(250);
      dispatch(setIsLatestLeoResponseExtensive(false));
    }
  }, [isLatestResponse, isResponseExtensive, dispatch]);

  useEffect(() => {
    handleExtensiveResponse();
  }, [handleExtensiveResponse]);

  return (
    <div className='my-6' data-testid='leo-response'>
      <div
        className='mb-2'
        onMouseEnter={() => debounce(() => setIsHovering(true), 100)()}
        onMouseLeave={() => debounce(() => setIsHovering(false), 100)()}
      >
        <NeoLeoChatBubble isFirst>
          <div
            className={cn(
              'neo-leo flex flex-row whitespace-pre-wrap word-break',
            )}
          >
            <Markdown content={msg.message!} msgId={msg.id} />
          </div>
          {showCreateTicket && (
            <div className='flex flex-row items-center mt-3'>
              <Button
                leftIcon={
                  !isCreatingTicket ? (
                    <FontAwesomeIcon
                      icon={faPlus}
                      className='text-reskin-primary-navy'
                      size='sm'
                    />
                  ) : undefined
                }
                onPress={() => dispatch(createTicket(msg.id!))}
                isSubmitting={isCreatingTicket}
                isDisabled={isCreatingTicket}
                label='Create Support Ticket'
              />
            </div>
          )}
        </NeoLeoChatBubble>
        <div
          className={cn(
            'w-full flex flex-row transition-all duration-300',
            isHovering ? 'h-auto mt-1' : 'pointer-events-none h-0',
          )}
        >
          {isHovering && !isDisclaimer && (
            <NeoLeoChatResponseActions {...msg} />
          )}
        </div>
      </div>
      {showCreateSupportTicket && (
        <NeoLeoChatBubble
          isLast={ContextOrder.Last === ContextEnum.CREATE_SUPPORT_TICKET}
        >
          <NeoLeoCreateSupportTicketMsgV2 session_id={msg.session_id} />
        </NeoLeoChatBubble>
      )}
      {showCallSupport && (
        <NeoLeoChatBubble
          isLast={ContextOrder.Last === ContextEnum.CALL_SUPPORT}
        >
          <NeoLeoCallSupportMsg
            session_id={msg.session_id}
            callSupportDetails={callSupportDetails}
          />
        </NeoLeoChatBubble>
      )}
      {hasShareablePosters && (
        <NeoLeoChatBubble
          isLast={ContextOrder.Last === ContextEnum.SHAREABLE_POSTERS}
        >
          <NeoLeoGenericShareables
            msgId={msg.id}
            shareables={shareablePosters}
          />
        </NeoLeoChatBubble>
      )}
      {hasTransactionItems && (
        <NeoLeoChatBubble
          isLast={ContextOrder.Last === ContextEnum.TRANSACTION_ITEMS}
        >
          <NeoLeoTxnOrListingItems msgContext={msgContext} />
        </NeoLeoChatBubble>
      )}
      {hasCourses && (
        <NeoLeoChatBubble isLast={ContextOrder.Last === ContextEnum.COURSES}>
          <NeoLeoCourses msgContext={msgContext} />
        </NeoLeoChatBubble>
      )}
      {hasLearningLinks && (
        <NeoLeoChatBubble
          isLast={ContextOrder.Last === ContextEnum.LEARNING_LINKS}
        >
          <NeoLeoLearningLinks msgContext={msgContext} />
        </NeoLeoChatBubble>
      )}
      <FeedbackNote feedbackNote={msgContext?.note} hasAdditionalInfo={false} />
    </div>
  );
};

export default NeoLeoResponse;
